import { useHistory, useRevert } from '../api/hooks'
import { Drawer } from '../components/Modal'
import { useUi } from '../store/ui'
import type { Revision } from '../api/types'

const ACTOR: Record<Revision['actor_type'], { glyph: string; label: string }> = {
  user: { glyph: '✎', label: 'Human edit' },
  ai: { glyph: '◆', label: 'AI translation' },
  api: { glyph: '⇅', label: 'API push' },
  import: { glyph: '⇩', label: 'Import' },
  revert: { glyph: '↺', label: 'Revert' },
}

/** Word-level diff reduced to common prefix/suffix — enough to see what moved. */
function diffWords(before: string, after: string) {
  const a = before.split(/(\s+)/)
  const b = after.split(/(\s+)/)
  let start = 0
  while (start < a.length && start < b.length && a[start] === b[start]) start += 1
  let end = 0
  while (
    end < a.length - start &&
    end < b.length - start &&
    a[a.length - 1 - end] === b[b.length - 1 - end]
  )
    end += 1
  return {
    prefix: a.slice(0, start).join(''),
    removed: a.slice(start, a.length - end).join(''),
    added: b.slice(start, b.length - end).join(''),
    suffix: a.slice(a.length - end).join(''),
  }
}

export function HistoryDrawer({
  platform,
  projectId,
}: {
  platform: string
  projectId: string | undefined
}) {
  const target = useUi((state) => state.historyFor)
  const close = useUi((state) => state.openHistory)
  const history = useHistory(target?.translationId ?? null)
  const revert = useRevert(platform, projectId)

  if (!target) return null

  return (
    <Drawer
      title={target.keyName}
      subtitle={`${target.locale} — revision history`}
      onClose={() => close(null)}
    >
      {history.isLoading && <p className="text-xs text-ink-600">Loading history…</p>}
      {history.isError && (
        <p role="alert" className="text-xs text-rose-800">
          {(history.error as Error).message}
        </p>
      )}
      {history.data && (
        <>
          <div className="mb-3 rounded border border-ink-200 bg-ink-50 p-2">
            <p className="label">Current value</p>
            <p className="mt-1 whitespace-pre-wrap text-xs text-ink-900">
              {history.data.translation.value}
            </p>
            <p className="mt-1 text-2xs text-ink-600">
              {history.data.translation.status}
              {history.data.translation.needs_review ? ' · needs review' : ''} · updated by{' '}
              {history.data.translation.updated_by}
            </p>
          </div>
          <ol className="space-y-2">
            {history.data.revisions.map((revision) => {
              const parts = diffWords(revision.old_value ?? '', revision.new_value)
              return (
                <li key={revision.id} className="rounded border border-ink-200 p-2">
                  <div className="flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5 text-2xs text-ink-600">
                      <span aria-hidden="true">{ACTOR[revision.actor_type].glyph}</span>
                      <span className="font-medium text-ink-800">
                        {ACTOR[revision.actor_type].label}
                      </span>
                      <span className="font-mono">{revision.old_status ?? '∅'}</span>
                      <span aria-hidden="true">→</span>
                      <span className="font-mono">{revision.new_status}</span>
                    </span>
                    <time className="text-2xs text-ink-600" dateTime={revision.created_at}>
                      {new Date(revision.created_at).toLocaleString()}
                    </time>
                  </div>
                  <p className="mt-1 whitespace-pre-wrap text-xs leading-snug">
                    <span className="text-ink-600">{parts.prefix}</span>
                    {parts.removed && (
                      <span className="bg-rose-100 text-rose-900 line-through">{parts.removed}</span>
                    )}
                    {parts.added && <span className="bg-emerald-100 text-emerald-900">{parts.added}</span>}
                    <span className="text-ink-600">{parts.suffix}</span>
                  </p>
                  <button
                    className="btn mt-1.5"
                    disabled={revert.isPending}
                    onClick={() => revert.mutate(revision.id)}
                  >
                    ↺ Revert to this value
                  </button>
                </li>
              )
            })}
            {history.data.revisions.length === 0 && (
              <li className="text-xs text-ink-600">No revisions recorded.</li>
            )}
          </ol>
        </>
      )}
    </Drawer>
  )
}
